/**
 * Firestore Utilities
 * 
 * This module provides all Firestore database operations for blog/portfolio management:
 * - Save blog/portfolio to Firestore
 * - Load blog/portfolio from Firestore
 * - Load all portfolios for a user
 * - Delete blog/portfolio
 * - Update blog/portfolio
 * 
 * Data structure:
 * users/{userId}/portfolios/{portfolioTitle} -> blog data
 */

import { db } from '../../../config/firebaseConfig';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';
import { compactPortfolioImagesForSave } from './htmlImageReferenceUtils.js';

const validateCollectionPath = (collectionPath) => {
  if (!Array.isArray(collectionPath) || collectionPath.length === 0) {
    throw new Error('Invalid Firestore collection path');
  }

  const hasMissingSegment = collectionPath.some(
    (segment) => segment === null || segment === undefined || segment === ''
  );

  if (hasMissingSegment) {
    throw new Error('Invalid Firestore collection path: missing required segment');
  }

  return collectionPath;
};

const validatePortfolioTitle = (portfolioTitle) => {
  if (portfolioTitle === null || portfolioTitle === undefined || portfolioTitle === '') {
    throw new Error('Invalid portfolio title');
  }

  return portfolioTitle;
};

const createCollectionRef = (collectionPath) => collection(db, ...validateCollectionPath(collectionPath));

const createPortfolioRef = (collectionPath, portfolioTitle) =>
  doc(db, ...validateCollectionPath(collectionPath), validatePortfolioTitle(portfolioTitle));

/**
 * Save a new portfolio/blog to Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} portfolioTitle - Title of the portfolio (used as document ID)
 * @param {Object} blogData - The blog/portfolio content data
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if save fails
 */
export const saveBlog = async (portfolioRef, portfolioTitle, blogData) => {
  try {
    const compactBlogData = compactPortfolioImagesForSave(blogData);

    await setDoc(portfolioRef, {
      ...compactBlogData,
      title: portfolioTitle,
      createdAt: new Date(),
      updatedAt: new Date(),
    }, { merge: true });

    return {
      success: true,
      message: `Blog "${portfolioTitle}" saved successfully`,
    };
  } catch (error) {
    console.error('Error saving blog:', error.message);
    throw new Error(`Failed to save blog: ${error.message}`);
  }
};



/**
 * Load a specific portfolio/blog from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} portfolioTitle - Title of the portfolio to load
 * @returns {Promise<Object|null>} - Portfolio data or null if not found
 * @throws {Error} - Firestore error if load fails
 */
export const loadBlog = async (portfolioRef) => {
  try {
    const docSnap = await getDoc(portfolioRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id,
      };
    }

    return null;
  } catch (error) {
    console.error('Error loading portfolio:', error.message);
    throw new Error(`Failed to load portfolio: ${error.message}`);
  }
};



/**
 * Load all portfolios for a specific user
 * 
 * @param {string} userId - User's unique ID
 * @returns {Promise<Array>} - Array of portfolio objects with metadata
 * @throws {Error} - Firestore error if load fails
 */
export const loadBlogs = async (portfoliosRef) => {
  try {
    const querySnapshot = await getDocs(portfoliosRef);

    const portfolios = [];
    querySnapshot.forEach((doc) => {
      portfolios.push({
        ...doc.data(),
        id: doc.id,
      });
    });

    // Sort by updatedAt in descending order (newest first)
    portfolios.sort((a, b) => {
      const dateA = a.updatedAt?.toDate?.() || new Date(a.updatedAt);
      const dateB = b.updatedAt?.toDate?.() || new Date(b.updatedAt);
      return dateB - dateA;
    });

    return portfolios;
  } catch (error) {
    console.error('Error loading portfolios:', error.message);
    throw new Error(`Failed to load portfolios: ${error.message}`);
  }
};



/**
 * Delete a specific portfolio/blog from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} portfolioTitle - Title of the portfolio to delete
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if delete fails
 */
export const deleteBlog = async (portfolioRef) => {
  try {
    await deleteDoc(portfolioRef);

    return {
      success: true,
      message: `Portfolio deleted successfully`,
    };
  } catch (error) {
    console.error('Error deleting portfolio:', error.message);
    throw new Error(`Failed to delete portfolio: ${error.message}`);
  }
};



/**
 * Update an existing portfolio/blog in Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} portfolioTitle - Title of the portfolio to update
 * @param {Object} updateData - Partial data to update
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if update fails
 */
export const updateBlog = async (portfolioRef, updateData) => {
  try {
    const compactUpdateData = compactPortfolioImagesForSave(updateData);

    await updateDoc(portfolioRef, {
      ...compactUpdateData,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: `Portfolio updated successfully`,
    };
  } catch (error) {
    console.error('Error updating portfolio:', error.message);
    throw new Error(`Failed to update portfolio: ${error.message}`);
  }
};


/**
 * Rename a portfolio (delete old and create new with same data)
 * 
 * @param {string} userId - User's unique ID
 * @param {string} oldTitle - Current portfolio title
 * @param {string} newTitle - New portfolio title
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Error if rename fails
 */
export const renameBlog = async (newTitle, oldTitle, portfolioRef) => {
  try {
    // Load existing portfolio data
    const portfolioData = await loadBlog(portfolioRef);

    if (!portfolioData) {
      throw new Error('Portfolio not found');
    }

    // Remove the id field before saving
    const { id, ...dataToSave } = portfolioData;

    // Save with new title
    await saveBlog(portfolioRef, newTitle, dataToSave);

    // Delete old portfolio
    await deleteBlog(portfolioRef);

    return {
      success: true,
      message: `Portfolio renamed from "${oldTitle}" to "${newTitle}"`,
    };
  } catch (error) {
    console.error('Error renaming portfolio:', error.message);
    throw new Error(`Failed to rename portfolio: ${error.message}`);
  }
};



/**
 * Check if a portfolio exists for a user
 * 
 * @param {string} userId - User's unique ID
 * @param {string} portfolioTitle - Title of the portfolio to check
 * @returns {Promise<boolean>} - True if portfolio exists, false otherwise
 */
export const blogExists = async (portfolioRef) => {
  try {
    const portfolio = await loadBlog(portfolioRef);
    return portfolio !== null;
  } catch (error) {
    console.error('Error checking portfolio existence:', error.message);
    return false;
  }
};



/**
 * Get portfolio count for a user
 * 
 * @param {string} userId - User's unique ID
 * @returns {Promise<number>} - Number of portfolios
 */
export const getBlogCount = async (portfolioRef) => {
  try {
    const portfolios = await loadBlogs(portfolioRef);
    return portfolios.length;
  } catch (error) {
    console.error('Error getting portfolio count:', error.message);
    return 0;
  }
};

export const savePortfolioAtPath = async (
  collectionPath,
  portfolioTitle,
  blogData,
  entityLabel = 'Portfolio'
) => {
  try {
    const portfolioRef = createPortfolioRef(collectionPath, portfolioTitle);

    await setDoc(
      portfolioRef,
      {
        ...blogData,
        title: portfolioTitle,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      { merge: true }
    );

    return {
      success: true,
      message: `${entityLabel} "${portfolioTitle}" saved successfully`,
    };
  } catch (error) {
    console.error(`Error saving ${entityLabel.toLowerCase()}:`, error.message);
    throw new Error(`Failed to save ${entityLabel.toLowerCase()}: ${error.message}`);
  }
};

export const loadPortfolioAtPath = async (collectionPath, portfolioTitle) => {
  try {
    const portfolioRef = createPortfolioRef(collectionPath, portfolioTitle);
    const docSnap = await getDoc(portfolioRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id,
      };
    }

    return null;
  } catch (error) {
    console.error('Error loading portfolio:', error.message);
    throw new Error(`Failed to load portfolio: ${error.message}`);
  }
};

export const loadPortfoliosAtPath = async (collectionPath) => {
  try {
    const portfoliosRef = createCollectionRef(collectionPath);
    const querySnapshot = await getDocs(portfoliosRef);

    const portfolios = [];
    querySnapshot.forEach((docSnap) => {
      portfolios.push({
        ...docSnap.data(),
        id: docSnap.id,
      });
    });

    portfolios.sort((a, b) => {
      const dateA = a.updatedAt?.toDate?.() || new Date(a.updatedAt);
      const dateB = b.updatedAt?.toDate?.() || new Date(b.updatedAt);
      return dateB - dateA;
    });

    return portfolios;
  } catch (error) {
    console.error('Error loading portfolios:', error.message);
    throw new Error(`Failed to load portfolios: ${error.message}`);
  }
};

export const deletePortfolioAtPath = async (
  collectionPath,
  portfolioTitle,
  entityLabel = 'Portfolio'
) => {
  try {
    const portfolioRef = createPortfolioRef(collectionPath, portfolioTitle);
    await deleteDoc(portfolioRef);

    return {
      success: true,
      message: `${entityLabel} deleted successfully`,
    };
  } catch (error) {
    console.error(`Error deleting ${entityLabel.toLowerCase()}:`, error.message);
    throw new Error(`Failed to delete ${entityLabel.toLowerCase()}: ${error.message}`);
  }
};

export const updatePortfolioAtPath = async (
  collectionPath,
  portfolioTitle,
  updateData,
  entityLabel = 'Portfolio'
) => {
  try {
    const portfolioRef = createPortfolioRef(collectionPath, portfolioTitle);

    await updateDoc(portfolioRef, {
      ...updateData,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: `${entityLabel} updated successfully`,
    };
  } catch (error) {
    console.error(`Error updating ${entityLabel.toLowerCase()}:`, error.message);
    throw new Error(`Failed to update ${entityLabel.toLowerCase()}: ${error.message}`);
  }
};

export const renamePortfolioAtPath = async (
  collectionPath,
  oldTitle,
  newTitle,
  entityLabel = 'Portfolio'
) => {
  try {
    if (oldTitle === newTitle) {
      return {
        success: true,
        message: `${entityLabel} already has the title "${newTitle}"`,
      };
    }

    const oldPortfolioRef = createPortfolioRef(collectionPath, oldTitle);
    const newPortfolioRef = createPortfolioRef(collectionPath, newTitle);
    const portfolioData = await loadBlog(oldPortfolioRef);

    if (!portfolioData) {
      throw new Error(`${entityLabel} not found`);
    }

    const { id, createdAt, updatedAt, ...dataToSave } = portfolioData;

    await setDoc(
      newPortfolioRef,
      {
        ...dataToSave,
        title: newTitle,
        createdAt: createdAt || new Date(),
        updatedAt: new Date(),
      },
      { merge: true }
    );

    await deleteDoc(oldPortfolioRef);

    return {
      success: true,
      message: `${entityLabel} renamed from "${oldTitle}" to "${newTitle}"`,
    };
  } catch (error) {
    console.error(`Error renaming ${entityLabel.toLowerCase()}:`, error.message);
    throw new Error(`Failed to rename ${entityLabel.toLowerCase()}: ${error.message}`);
  }
};

export const portfolioExistsAtPath = async (collectionPath, portfolioTitle) => {
  try {
    const portfolio = await loadPortfolioAtPath(collectionPath, portfolioTitle);
    return portfolio !== null;
  } catch (error) {
    console.error('Error checking portfolio existence:', error.message);
    return false;
  }
};

export const getPortfolioCountAtPath = async (collectionPath) => {
  try {
    const portfolios = await loadPortfoliosAtPath(collectionPath);
    return portfolios.length;
  } catch (error) {
    console.error('Error getting portfolio count:', error.message);
    return 0;
  }
};





